import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isArray } from 'lodash';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { login, loginWithToken } from '@greenkub-fronts/domain/auth';
import { RootState } from '@greenkub-fronts/shared/interface';
import { AppDispatch } from '@greenkub-fronts/shared/interface';

import { usePostHog } from 'posthog-js/react';
import TagManager from 'react-gtm-module';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

type FormData = {
  email: string;
  password: string;
};

const schema = yup
  .object()
  .shape({
    email: yup.string().email().required('Un email est requis'),
    password: yup.string().required('Le mot de passe est requis'),
  })
  .required();

export function useLoginFormFeature() {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const { loginErrorMsg, isFetching } = useSelector(
    (state: RootState) => state.auth
  );

  const split = location.search.split('&');

  let email = '';
  let password = '';

  if (isArray(split) && split.length > 1 && split[0].includes('email')) {
    email = split[0].replace('?email=', '');
  }

  if (isArray(split) && split.length > 1 && split[1].includes('security')) {
    password = split[1].replace('security=', '');
  }

  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormData>({
    defaultValues: {
      email,
      password,
    },
    resolver: yupResolver(schema),
  });

  const postHog = usePostHog();

  const onSubmit: SubmitHandler<FormData> = (data) => {
    dispatch(login({ ...data }));
    sendToAnalytics(data.email);
  };

  const sendToAnalytics = (email: string) => {
    postHog?.capture('login', {
      email,
    });
    TagManager.dataLayer({
      dataLayer: {
        event: 'login',
        email,
      },
    });
  };

  const onPasswordVisibleToggle = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  return {
    handleSubmit,
    errors,
    onSubmit,
    isPasswordVisible,
    onPasswordVisibleToggle,
    loginErrorMsg,
    isFetching,
    control,
  };
}

export default useLoginFormFeature;
